/**
 * The admin action log.
 *
 * `GET /admin/logs` has existed since the audit trail was added, and
 * `permissions.ts` has gated a `logs` section for as long -- but the shell's
 * nav list did not carry the entry, so the gate was dead code and the endpoint
 * had no reader. Every admin mutation writes here; this is where an operator
 * finds out who changed what.
 */

import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Skeleton } from "@shared-ui/skeleton";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/ui/empty-state";
import { DataTable } from "@/features/catalog/components/data-table";
import { useDebounce } from "@/lib/use-debounce";
import {
	type AdminActionLog,
	listAdminActionLogs,
} from "../services/logs-api.service";

const PAGE_SIZE = 50;

function MetadataCell({ log }: { log: AdminActionLog }) {
	const entries = Object.entries(log.metadata ?? {});
	if (entries.length === 0) {
		return <span className="text-muted-foreground">—</span>;
	}

	return (
		<div className="grid gap-0.5">
			{entries.map(([key, value]) => (
				<span key={key} className="font-mono text-xs">
					{key}: {JSON.stringify(value)}
				</span>
			))}
		</div>
	);
}

export function LogsDashboardPage() {
	const [action, setAction] = useState("");
	const [subjectType, setSubjectType] = useState("");
	// Cursor stack, so "Back" returns to the page the operator came from rather
	// than to the start. The API is forward-only cursors.
	const [cursors, setCursors] = useState<(string | null)[]>([null]);

	const debouncedAction = useDebounce(action, 300);
	const debouncedSubjectType = useDebounce(subjectType, 300);
	const cursor = cursors[cursors.length - 1];

	const logsQuery = useQuery({
		queryKey: [
			"admin-logs",
			debouncedAction,
			debouncedSubjectType,
			cursor ?? "first",
		],
		queryFn: () =>
			listAdminActionLogs({
				action: debouncedAction,
				subjectType: debouncedSubjectType,
				limit: PAGE_SIZE,
				cursor: cursor ?? undefined,
			}),
		placeholderData: keepPreviousData,
	});

	function resetPaging() {
		setCursors([null]);
	}

	const logs = logsQuery.data?.logs ?? [];
	const nextCursor = logsQuery.data?.nextCursor ?? null;

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
					Audit
				</p>
				<h2 className="text-2xl font-semibold tracking-tight">
					Admin action log
				</h2>
				<p className="text-sm text-muted-foreground">
					Every administrative mutation, with the operator who made it and the
					reason they gave.
				</p>
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Recorded actions</CardTitle>
					<CardDescription>
						Filter by action name (for example <code>user.type_changed</code>)
						or subject type (<code>user</code>, <code>designer_profile</code>).
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-3 md:grid-cols-2">
						<Input
							placeholder="Action"
							value={action}
							onChange={(event) => {
								setAction(event.target.value);
								resetPaging();
							}}
						/>
						<Input
							placeholder="Subject type"
							value={subjectType}
							onChange={(event) => {
								setSubjectType(event.target.value);
								resetPaging();
							}}
						/>
					</div>

					{logsQuery.isLoading ? (
						<Skeleton className="h-64 w-full rounded-2xl" />
					) : logs.length === 0 ? (
						<EmptyState title="No admin actions match these filters." />
					) : (
						<DataTable
							rows={logs}
							rowKey={(log) => log.id}
							isRefreshing={logsQuery.isFetching}
							columns={[
								{
									header: "When",
									cell: (log) => new Date(log.createdAt).toLocaleString(),
									cellClassName: "whitespace-nowrap text-muted-foreground",
								},
								{
									header: "Actor",
									cell: (log) => log.actorUserPhoneSnapshot,
									cellClassName: "font-mono text-xs",
								},
								{
									header: "Action",
									cell: (log) => log.action,
									cellClassName: "font-medium",
								},
								{
									header: "Subject",
									cell: (log) => (
										<>
											<span className="text-muted-foreground">
												{log.subjectType}
											</span>
											<br />
											<span className="font-mono text-xs">{log.subjectId}</span>
										</>
									),
								},
								{
									header: "Reason",
									cell: (log) =>
										log.reason || (
											<span className="text-muted-foreground">—</span>
										),
								},
								{
									header: "Metadata",
									cell: (log) => <MetadataCell log={log} />,
								},
							]}
						/>
					)}

					<div className="flex items-center justify-between">
						<Button
							variant="outline"
							disabled={cursors.length === 1}
							onClick={() => setCursors((prev) => prev.slice(0, -1))}
						>
							Back
						</Button>
						<Button
							variant="outline"
							disabled={!nextCursor}
							onClick={() =>
								setCursors((prev) =>
									nextCursor ? [...prev, nextCursor] : prev,
								)
							}
						>
							Next
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
